// Cycle-16 doc-attachment card. Mirrors `PingDocAttachmentCard`
// (iOS-A) / `DocAttachmentCard` (AND-A). Renders one of three states
// from the Figma `Doc Attachment w/ preview` (`3412:4897`) /
// `Doc attachment / failed` (`3412:4903`) frames.
//
// Two Figma layouts share the same primitive:
//   • compact (3412:4908 / 3412:4932)        — 268×56 single row, used
//                                              by uploading / failed
//                                              states and self-bubble
//   • with-preview (I3412:4900;577:3675)     — 300×254 column with a
//                                              292×199 light thumbnail
//                                              tile + 292×43 footer.
//                                              Rendered when the host
//                                              supplies `pageCount`
//                                              (proxy for "has preview")
//
// Outer geometry (both layouts):
//   • radius 16, padding 4, gap 4
//   • indigo `#5C7BF0` fill for self, dark `surface.secondary` for
//     received / failed
//   • inner overlay frame (radius 12, rgba(0,0,0,0.2)) wraps the row

import React, { useMemo, useRef, useState } from 'react';
import { Image, Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { useTileMaxWidth } from '../tileConstraint';
import { Check, CheckCheck, Info, RotateCw } from 'lucide-react-native';
import { tokens } from '../../design-system/tokens';
import { formatBytes } from '../messageFormatters';
import type { DocAttachmentState } from './DocAttachmentState';
import { ReadByPopover, type ReadByReader } from '../ReadByPopover';

export interface DocAttachmentCardProps {
  state: DocAttachmentState;
  filename: string;
  mimeType: string;
  byteSize: number;
  pageCount?: number | undefined;
  progress?: number | undefined;
  /**
   * Optional — when present AND `state === 'uploaded'` the card renders
   * the wide "with-preview" layout (Figma 3412:4897) with a 292×199
   * cover-cropped thumbnail of the first document page.
   */
  previewUri?: string | undefined;
  /** Optional — when true the card renders as `isSelf` (indigo). */
  isSelf?: boolean | undefined;
  /** Pre-formatted send time (e.g. "12:33") in the preview footer. */
  timeLabel?: string | undefined;
  /** Own + delivered → muted double-check beside the footer time (Figma 7038:1838). */
  delivered?: boolean | undefined;
  /** Own + read → green double-check (takes precedence over `delivered`). */
  read?: boolean | undefined;
  onTapPreview: () => void;
  onRetry?: (() => void) | undefined;
  /** Group chats only — resolved reader list for the "Read by" popover. */
  readers?: ReadByReader[] | undefined;
  testID?: string | undefined;
}

function fileExtension(filename: string, mimeType: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot >= 0 && dot < filename.length - 1) {
    return filename.slice(dot + 1).toUpperCase();
  }
  if (mimeType.includes('/')) {
    return (mimeType.split('/')[1] ?? '').toUpperCase();
  }
  return 'FILE';
}

export const DocAttachmentCard = React.memo<DocAttachmentCardProps>(
  ({ state, filename, mimeType, byteSize, pageCount, progress, previewUri, isSelf, timeLabel, delivered, read, onTapPreview, onRetry, readers, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];
    // Thread side panel: cap the tile to the provided width.
    const tileMax = useTileMaxWidth();
    const tileMaxStyle = tileMax !== null ? { maxWidth: tileMax } : null;

    const ext = useMemo(() => fileExtension(filename, mimeType), [filename, mimeType]);
    const sizeText = useMemo(() => formatBytes(byteSize), [byteSize]);

    const isFailed = state === 'failed';
    const isUploading = state === 'uploading';

    // Figma 3412:4908 / 3412:4932: bubble fill is the indigo accent
    // when self, dark `surface.secondary` otherwise. Failed does NOT
    // recolour the bubble — only the error caption below.
    const bubbleBackground = isSelf
      ? colors.accent.brand
      : colors.surface.elevated;
    const foreground = isSelf ? colors.text.onAccent : colors.text.primary;
    const subtitleColor = isSelf
      ? 'rgba(255, 255, 255, 0.6)'
      : colors.text.secondary;

    // Figma 3412:4909: inner overlay is black 20 % on top of the
    // bubble fill. Keeps both light and dark schemes consistent.
    const innerOverlay = 'rgba(0, 0, 0, 0.2)';

    const subtitleParts: string[] = [];
    subtitleParts.push(`Document .${ext}`);
    subtitleParts.push(sizeText);
    if (pageCount !== undefined && pageCount > 0) {
      subtitleParts.push(pageCount === 1 ? '1 page' : `${pageCount} pages`);
    }
    if (isUploading && progress !== undefined) {
      const pct = Math.min(100, Math.max(0, Math.round(progress * 100)));
      subtitleParts.push(`${pct}%`);
    }

    const accessibilityLabel = useMemo(() => {
      const stateText = state === 'uploaded' ? 'attachment' : state;
      return `Document ${stateText}, ${ext} file, ${sizeText}`;
    }, [state, ext, sizeText]);

    const titleText = isFailed ? 'Failed to send' : filename;

    // Figma 3412:4897 — wide "with-preview" layout. Only rendered for an
    // uploaded doc that supplied a page preview. Self bubbles use the
    // indigo accent fill; received bubbles use the dark `#222429`
    // (`surface.elevated`) tertiary surface from the audit.
    const showPreview = previewUri !== undefined && state === 'uploaded';
    const tickRef = useRef<View | null>(null);
    const [readersOpen, setReadersOpen] = useState(false);
    const hasReaders = isSelf === true && (readers?.length ?? 0) > 0;

    if (showPreview) {
      const previewWrapFill = isSelf ? colors.accent.brand : colors.surface.elevated;
      // Pill fill — translucent white when self, dark tertiary surface
      // for received, per the audit.
      const pillFill = isSelf ? 'rgba(244, 244, 245, 0.08)' : colors.surface.elevated;

      return (
        <>
          <Pressable
            onPress={onTapPreview}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            accessibilityHint="Preview document"
            style={[styles.previewCard, tileMaxStyle, { backgroundColor: previewWrapFill }]}
            testID={testID}
          >
            <View style={styles.previewThumb}>
              <Image
                source={{ uri: previewUri }}
                style={styles.previewImage}
                resizeMode="cover"
                accessibilityIgnoresInvertColors
              />
            </View>
            <View style={styles.previewFooter}>
              <View style={styles.previewMeta}>
                <Text style={[styles.previewTitle, { color: foreground }]} numberOfLines={1}>
                  {filename}
                </Text>
                <Text style={[styles.previewSize, { color: subtitleColor }]} numberOfLines={1}>
                  {sizeText}
                </Text>
              </View>
              <View style={styles.previewRight}>
                <View style={[styles.extPill, { backgroundColor: pillFill }]}>
                  <Text style={[styles.extPillText, { color: foreground }]} numberOfLines={1}>
                    .{ext}
                  </Text>
                </View>
                {timeLabel !== undefined ? (
                  hasReaders ? (
                    <Pressable
                      ref={tickRef}
                      style={styles.previewTimeRow}
                      onPress={(e) => {
                        e.stopPropagation();
                        setReadersOpen(true);
                      }}
                      hitSlop={8}
                      accessibilityRole="button"
                      accessibilityLabel="See who read this message"
                    >
                      <Text style={[styles.previewTime, { color: subtitleColor }]}>
                        {timeLabel}
                      </Text>
                      {isSelf && (delivered === true || read === true) ? (
                        <CheckCheck
                          size={12}
                          strokeWidth={2}
                          color={read === true ? '#3CCB4F' : subtitleColor}
                        />
                      ) : isSelf ? (
                        <Check size={12} strokeWidth={2} color={subtitleColor} />
                      ) : null}
                    </Pressable>
                  ) : (
                    <View style={styles.previewTimeRow}>
                      <Text style={[styles.previewTime, { color: subtitleColor }]}>
                        {timeLabel}
                      </Text>
                      {isSelf && (delivered === true || read === true) ? (
                        <CheckCheck
                          size={12}
                          strokeWidth={2}
                          color={read === true ? '#3CCB4F' : subtitleColor}
                        />
                      ) : isSelf ? (
                        <Check size={12} strokeWidth={2} color={subtitleColor} />
                      ) : null}
                    </View>
                  )
                ) : null}
              </View>
            </View>
          </Pressable>
          <ReadByPopover
            anchorRef={tickRef as { current: unknown }}
            open={readersOpen}
            onClose={() => setReadersOpen(false)}
            readers={readers ?? []}
          />
        </>
      );
    }

    return (
      <>
        <Pressable
          ref={tickRef}
          onPress={isFailed ? onRetry : onTapPreview}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          accessibilityHint={isFailed ? 'Retry upload' : 'Preview document'}
          style={[styles.card, tileMaxStyle, { backgroundColor: bubbleBackground }]}
          testID={testID}
        >
          <View style={[styles.inner, { backgroundColor: innerOverlay }]}>
            <View style={styles.row}>
              <View
                style={[
                  styles.leadingIcon,
                  { backgroundColor: 'rgba(244, 244, 245, 0.08)' },
                ]}
              >
                <RotateCw size={14} color={foreground} />
              </View>
              <View style={styles.body}>
                <Text style={[styles.title, { color: foreground }]} numberOfLines={1}>
                  {titleText}
                </Text>
                <Text style={[styles.subtitle, { color: subtitleColor }]} numberOfLines={1}>
                  {subtitleParts.join(' · ')}
                </Text>
              </View>
            </View>
          </View>
          {isFailed ? (
            <View style={styles.metaRow}>
              <Text style={[styles.errorText, { color: '#FF5353' }]}>
                Error sending attachment.
              </Text>
              <Info size={10} color="#FF5353" />
            </View>
          ) : null}
        </Pressable>
        <ReadByPopover
          anchorRef={tickRef as { current: unknown }}
          open={readersOpen}
          onClose={() => setReadersOpen(false)}
          readers={readers ?? []}
        />
      </>
    );
  },
);

DocAttachmentCard.displayName = 'DocAttachmentCard';

const styles = StyleSheet.create({
  // Outer card — Figma 3412:4908 (268×56, radius 16, padding 4, gap 4).
  card: {
    width: 268,
    maxWidth: '100%',
    padding: tokens.spacing.xs,
    borderRadius: 16,
    gap: tokens.spacing.xs,
  },
  // Inner overlay — Figma 3412:4909 (260×48, radius 12, padding 4).
  inner: {
    borderRadius: 12,
    paddingHorizontal: tokens.spacing.xs,
    paddingVertical: tokens.spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  // Leading icon — Figma 3412:4903 (28×28 circular retry/icon button,
  // pill radius, translucent white fill, RotateCw glyph).
  leadingIcon: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flexShrink: 1,
    flexGrow: 1,
  },
  // Title — Figma 3412:4913 / I3412:4900;577:3681 (12 pt Bold / 14 pt
  // Bold). Compact failed bubble uses 12 pt; with-preview uses 14 pt.
  // Settling on 14 pt matches the larger document name display.
  title: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '700',
  } as TextStyle,
  // Subtitle — Figma 3412:4915 / 3412:4916 (12 pt Regular @40 %).
  subtitle: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 12,
    lineHeight: 16,
    marginTop: 2,
  },
  // Error caption row — Figma 3412:4903: LEFT-aligned, rose text + a
  // 10×10 info-circle icon trailing the text, gap 4.
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.xs,
  },
  // Error caption — Figma 3412:4921 (10 pt Lato 400, rose).
  errorText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 10,
    lineHeight: 14,
  },

  // ── with-preview layout (Figma 3412:4897) ──────────────────────────
  // Outer wrap — 300 wide, radius 16, padding 4, gap 4.
  previewCard: {
    width: 300,
    maxWidth: '100%',
    padding: tokens.spacing.xs,
    borderRadius: 16,
    gap: tokens.spacing.xs,
  },
  // Thumbnail tile — ~292×199, light fill, radius 12, cover-cropped.
  previewThumb: {
    width: '100%',
    height: 199,
    borderRadius: 12,
    backgroundColor: '#F5F6F8',
    overflow: 'hidden',
  },
  previewImage: {
    width: '100%',
    height: '100%',
  },
  // Footer row — paddingLeft 12, gap 8, filename + size left / ext pill
  // right.
  previewFooter: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacing.sm,
    paddingLeft: tokens.spacing.md,
  },
  previewMeta: {
    flexShrink: 1,
    flexGrow: 1,
  },
  // Right column of the preview footer — ext pill over the send time.
  previewRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  previewTimeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  previewTime: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 10,
    lineHeight: 14,
  },
  // Filename — 14 pt Bold #F5F6F8.
  previewTitle: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '700',
  } as TextStyle,
  // Size — 12 pt, text.secondary.
  previewSize: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 12,
    lineHeight: 16,
    marginTop: 2,
  },
  // Ext pill — height 21, pill radius, padding H8 V2.
  extPill: {
    height: 21,
    borderRadius: tokens.radius.pill,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xxs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  extPillText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '700',
  } as TextStyle,
});
